export const PERIOD_TO_API = {
  Oggi: 'oggi',
  'Questa settimana': 'settimana',
  'Questo mese': 'mese',
  'Anno corrente': 'anno',
  'Personalizzato…': 'custom',
};

export const COMPARE_TO_API = {
  'Nessun confronto': 'none',
  'Periodo precedente': 'precedente',
  'Anno precedente': 'annoCorrispondente',
};

export const CLIENTI_PERIOD_TO_API = {
  Oggi: 'oggi',
  Mensile: 'mensile',
  Trimestrale: 'trimestrale',
  Semestrale: 'semestrale',
  Annuale: 'annuale',
};

const COMPARE_DELTA_LABEL = {
  none: null,
  precedente: 'vs periodo precedente',
  anno: 'vs anno precedente',
  annoCorrispondente: 'vs stesso periodo anno scorso',
  custom: 'vs periodo personalizzato',
};

const EMPTY_GIORNI = [
  { label: 'Lun', count: 0 },
  { label: 'Mar', count: 0 },
  { label: 'Mer', count: 0 },
  { label: 'Gio', count: 0 },
  { label: 'Ven', count: 0 },
  { label: 'Sab', count: 0 },
  { label: 'Dom', count: 0 },
];

export const EMPTY_STATISTICS_SCOPE = {
  fatturato: 0,
  entrate: 0,
  uscite: 0,
  utile: 0,
  prenotazioni: 0,
  scontrinoMedio: 0,
  disdette: { totale: 0, daCliente: 0, daSalone: 0, prenotazioniTotale: 0 },
  team: [],
  teamPrev: null,
  canali: [],
  servizi: [],
  canaliSegments: null,
  serviziSegments: null,
  canaliDeltas: null,
  serviziDeltas: null,
  queueDeltas: null,
  disdetteDeltas: null,
  ricorrentiDeltas: null,
  deltas: null,
  deltaLabel: null,
  queue: [],
  queueSummary: {
    items: [
      ['In coda', 0, 'queue'],
      ['Recuperati', 0, 'recovered'],
    ],
    totalQueue: 0,
    totalRecovered: 0,
  },
  disdetteSummary: [],
  disdetteSegments: null,
  finEntrateBreakdown: [],
  finUsciteBreakdown: [],
  ricorrenti: {
    totale: 0,
    monthsInPeriod: 1,
    team: [],
  },
};

export const EMPTY_CLIENTI_SCOPE = {
  clientiAttivi: 0,
  clientiTotali: 0,
  prenotazioni: 0,
  fatturato: 0,
  clientiRischioAbbandono: 0,
  clientiPericolosiCount: 0,
  giorni: EMPTY_GIORNI,
  byPoltrona: [],
};

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

export const isCustomFilter = (period, compare) =>
  PERIOD_TO_API[period] === 'custom' || COMPARE_TO_API[compare] === 'custom';

export const toPoltroneParam = (poltrona) =>
  !poltrona || poltrona === 'tutte' ? 'all' : String(poltrona);

export const getCompareDeltaLabel = (compareKey) =>
  COMPARE_DELTA_LABEL[compareKey] || null;

/** Match BE detectGroupBy for queue chart copy. */
export const detectQueueGroupBy = (dataInizio, dataFine) => {
  if (!dataInizio || !dataFine) return 'month';
  const start = new Date(`${dataInizio}T00:00:00`);
  const end = new Date(`${dataFine}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 'month';
  const days = Math.round((end - start) / 86400000) + 1;
  if (days <= 1) return 'day';
  if (days <= 7) return 'weekday';
  if (days <= 45) return 'week';
  return 'month';
};

export const pctDelta = (current, previous) => {
  const prev = toNumber(previous);
  if (!prev) return null;

  const pct = Math.round(((toNumber(current) - prev) / Math.abs(prev)) * 100);
  if (!pct) return { pct: 0, direction: 'up' };

  return {
    pct: Math.abs(pct),
    direction: pct < 0 ? 'down' : 'up',
  };
};

const toCategoryTuples = (items) => {
  if (!Array.isArray(items)) return [];
  return items
    .map((item) => {
      if (Array.isArray(item)) return item;
      if (!item || typeof item !== 'object') return null;
      return [item.label, item.pct ?? 0, item.colorKey, item.count];
    })
    .filter(Boolean)
    .filter((row) => row[0] !== 'App privata' || toNumber(row[3] ?? row[1]) > 0);
};

const categoryCountMap = (items) => {
  if (!Array.isArray(items)) return {};
  return items.reduce((acc, item) => {
    if (Array.isArray(item)) {
      acc[item[0]] = toNumber(item[3] ?? item[1]);
      return acc;
    }
    acc[item.label] = toNumber(item.count);
    return acc;
  }, {});
};

const buildCategoryDeltas = (currentItems, prevItems) => {
  if (!prevItems) return null;
  const prevMap = categoryCountMap(prevItems);
  const current = Array.isArray(currentItems) ? currentItems : [];
  return current.reduce((acc, item) => {
    const label = Array.isArray(item) ? item[0] : item?.label;
    if (!label) return acc;
    const count = Array.isArray(item) ? toNumber(item[3] ?? item[1]) : toNumber(item.count);
    const delta = pctDelta(count, prevMap[label] ?? 0);
    if (delta) acc[label] = delta;
    return acc;
  }, {});
};

const buildMetricDeltas = (current, prev) => {
  if (!prev) return null;
  return {
    fatturato: pctDelta(current.fatturato, prev.fatturato),
    entrate: pctDelta(current.entrate, prev.entrate),
    uscite: pctDelta(current.uscite, prev.uscite),
    utile: pctDelta(current.utile, prev.utile),
    prenotazioni: pctDelta(current.prenotazioni, prev.prenotazioni),
    scontrinoMedio: pctDelta(current.scontrinoMedio, prev.scontrinoMedio),
  };
};

const renormalizeCategoryTuples = (items) => {
  const rows = (items || []).filter((row) => Array.isArray(row) && row[0] !== 'Ricorrenti');
  const total = rows.reduce((sum, row) => sum + toNumber(row[3] ?? row[1]), 0);
  if (!total) return rows.map((row) => [row[0], 0, row[2], toNumber(row[3] ?? 0)]);

  const next = rows.map((row) => {
    const count = toNumber(row[3] ?? row[1]);
    return [row[0], Math.round((count / total) * 100), row[2], count];
  });
  const diff = 100 - next.reduce((sum, row) => sum + row[1], 0);
  if (diff && next.length) {
    const maxIdx = next.reduce((best, row, idx) => (row[3] > next[best][3] ? idx : best), 0);
    next[maxIdx][1] = Math.max(0, next[maxIdx][1] + diff);
  }
  return next;
};

const buildRecurringDeltas = (current, prev) => {
  if (!prev) return null;
  return {
    totale: pctDelta(current?.ricorrenti?.totale, prev?.ricorrenti?.totale),
  };
};

const buildQueueDeltas = (current, prev) => {
  if (!prev) return null;
  return {
    queue: pctDelta(current?.queueSummary?.totalQueue, prev?.queueSummary?.totalQueue),
    recovered: pctDelta(current?.queueSummary?.totalRecovered, prev?.queueSummary?.totalRecovered),
  };
};

const buildDisdetteDeltas = (current, prev) => {
  if (!prev) return null;
  return {
    totale: pctDelta(current?.disdette?.totale, prev?.disdette?.totale),
    daCliente: pctDelta(current?.disdette?.daCliente, prev?.disdette?.daCliente),
    daSalone: pctDelta(current?.disdette?.daSalone, prev?.disdette?.daSalone),
  };
};

export const mapFinanceToScope = (data, { compareKey } = {}) => {
  if (!data) {
    return {
      fatturato: EMPTY_STATISTICS_SCOPE.fatturato,
      entrate: EMPTY_STATISTICS_SCOPE.entrate,
      uscite: EMPTY_STATISTICS_SCOPE.uscite,
      utile: EMPTY_STATISTICS_SCOPE.utile,
      prenotazioni: EMPTY_STATISTICS_SCOPE.prenotazioni,
      scontrinoMedio: EMPTY_STATISTICS_SCOPE.scontrinoMedio,
      team: EMPTY_STATISTICS_SCOPE.team,
      teamPrev: EMPTY_STATISTICS_SCOPE.teamPrev,
      finEntrateBreakdown: EMPTY_STATISTICS_SCOPE.finEntrateBreakdown,
      finUsciteBreakdown: EMPTY_STATISTICS_SCOPE.finUsciteBreakdown,
      deltas: null,
      deltaLabel: getCompareDeltaLabel(compareKey),
    };
  }

  const useCompare = Boolean(compareKey && compareKey !== 'none');
  const prev = useCompare ? data.prev || null : null;
  return {
    fatturato: toNumber(data.fatturato),
    entrate: toNumber(data.entrate),
    uscite: toNumber(data.uscite),
    utile: toNumber(data.utile),
    prenotazioni: toNumber(data.prenotazioni),
    scontrinoMedio: toNumber(data.scontrinoMedio),
    team: Array.isArray(data.team) ? data.team : [],
    teamPrev: Array.isArray(prev?.team) ? prev.team : null,
    finEntrateBreakdown: Array.isArray(data.finEntrateBreakdown) ? data.finEntrateBreakdown : [],
    finUsciteBreakdown: Array.isArray(data.finUsciteBreakdown) ? data.finUsciteBreakdown : [],
    deltas: buildMetricDeltas(data, prev),
    deltaLabel: getCompareDeltaLabel(compareKey || data.compare?.key),
  };
};

export const mapBookingsToScope = (data, { compareKey } = {}) => {
  if (!data) {
    return {
      canali: EMPTY_STATISTICS_SCOPE.canali,
      servizi: EMPTY_STATISTICS_SCOPE.servizi,
      canaliSegments: EMPTY_STATISTICS_SCOPE.canaliSegments,
      serviziSegments: EMPTY_STATISTICS_SCOPE.serviziSegments,
      canaliDeltas: null,
      serviziDeltas: null,
      disdetteDeltas: null,
      ricorrentiDeltas: null,
      disdette: EMPTY_STATISTICS_SCOPE.disdette,
      disdetteSummary: EMPTY_STATISTICS_SCOPE.disdetteSummary,
      disdetteSegments: EMPTY_STATISTICS_SCOPE.disdetteSegments,
      ricorrenti: EMPTY_STATISTICS_SCOPE.ricorrenti,
    };
  }

  const useCompare = Boolean(compareKey && compareKey !== 'none');
  const prev = useCompare ? data.prev || null : null;
  const canali = renormalizeCategoryTuples(toCategoryTuples(data.canali));
  const canaliPrev = prev?.canali
    ? renormalizeCategoryTuples(toCategoryTuples(prev.canali))
    : null;
  const servizi = renormalizeCategoryTuples(toCategoryTuples(data.servizi));
  const serviziPrev = prev?.servizi
    ? renormalizeCategoryTuples(toCategoryTuples(prev.servizi))
    : null;

  return {
    canali,
    servizi,
    canaliSegments: data.canaliSegments || null,
    serviziSegments: data.serviziSegments || null,
    canaliDeltas: buildCategoryDeltas(canali, canaliPrev),
    serviziDeltas: buildCategoryDeltas(servizi, serviziPrev),
    disdetteDeltas: buildDisdetteDeltas(data, prev),
    ricorrentiDeltas: buildRecurringDeltas(data, prev),
    disdette: {
      totale: toNumber(data.disdette?.totale),
      daCliente: toNumber(data.disdette?.daCliente),
      daSalone: toNumber(data.disdette?.daSalone),
      prenotazioniTotale: toNumber(data.disdette?.prenotazioniTotale),
    },
    disdetteSummary: Array.isArray(data.disdetteSummary) ? data.disdetteSummary : [],
    disdetteSegments: data.disdetteSegments || null,
    ricorrenti: {
      totale: toNumber(data.ricorrenti?.totale),
      monthsInPeriod: Math.max(1, toNumber(data.ricorrenti?.monthsInPeriod) || 1),
      team: Array.isArray(data.ricorrenti?.team) ? data.ricorrenti.team : [],
    },
  };
};

export const mapQueueToScope = (data, { compareKey } = {}) => {
  if (!data) {
    return {
      queue: EMPTY_STATISTICS_SCOPE.queue,
      queueSummary: EMPTY_STATISTICS_SCOPE.queueSummary,
      queueDeltas: null,
    };
  }

  const useCompare = Boolean(compareKey && compareKey !== 'none');
  const prev = useCompare ? data.prev || null : null;
  return {
    queue: Array.isArray(data.queue) ? data.queue : [],
    queueSummary: {
      ...EMPTY_STATISTICS_SCOPE.queueSummary,
      ...(data.queueSummary || {}),
      items: Array.isArray(data.queueSummary?.items)
        ? data.queueSummary.items
        : EMPTY_STATISTICS_SCOPE.queueSummary.items,
    },
    queueDeltas: buildQueueDeltas(data, prev),
  };
};

/** @deprecated Prefer mergeStatisticsScope with section payloads */
export const mapDashboardToScope = (data) => {
  if (!data) return EMPTY_STATISTICS_SCOPE;
  return mergeStatisticsScope(data, data, data, { compareKey: data.compare?.key });
};

export const mapClientsToScope = (data, poltronaId = 'tutte') => {
  if (!data) return EMPTY_CLIENTI_SCOPE;

  const byPoltrona = Array.isArray(data.byPoltrona) ? data.byPoltrona : [];
  const source =
    poltronaId && poltronaId !== 'tutte'
      ? byPoltrona.find((row) => String(row.id) === String(poltronaId)) || null
      : null;

  const giorniSource = Array.isArray(source?.giorni)
    ? source.giorni
    : Array.isArray(data.giorni)
      ? data.giorni
      : [];
  const countByLabel = new Map(
    giorniSource.map((day) => [day?.label, toNumber(day?.count)]),
  );

  return {
    clientiAttivi: toNumber(source ? source.clientiAttivi : data.clientiAttivi),
    clientiTotali: toNumber(source ? source.clientiTotali : data.clientiTotali),
    prenotazioni: toNumber(source ? source.prenotazioni : data.prenotazioni),
    fatturato: toNumber(source ? source.fatturato : data.fatturato),
    clientiRischioAbbandono: toNumber(
      source ? source.clientiRischioAbbandono : data.clientiRischioAbbandono,
    ),
    clientiPericolosiCount: toNumber(
      source ? source.clientiPericolosiCount : data.clientiPericolosiCount,
    ),
    giorni: EMPTY_GIORNI.map((day, index) => ({
      label: day.label,
      count: countByLabel.has(day.label)
        ? countByLabel.get(day.label)
        : toNumber(giorniSource[index]?.count),
    })),
    byPoltrona,
  };
};

const segmentValue = (segments, poltronaId) => {
  if (!Array.isArray(segments)) return 0;
  const match = segments.find((seg) => String(seg.id) === String(poltronaId));
  return toNumber(match?.value);
};

const filterCategoryItemsByPoltrona = (items, segments, poltronaId) => {
  if (!Array.isArray(items)) return [];
  if (!segments) return items;
  return items.map((item) => {
    if (Array.isArray(item)) {
      const label = item[0];
      return [label, item[1], item[2], segmentValue(segments[label], poltronaId)];
    }
    return {
      ...item,
      count: segmentValue(segments[item.label], poltronaId),
    };
  });
};

const filterBookingsSectionByPoltrona = (payload, poltronaId) => {
  if (!payload) return payload;

  const canali = filterCategoryItemsByPoltrona(
    payload.canali,
    payload.canaliSegments,
    poltronaId,
  );
  const servizi = filterCategoryItemsByPoltrona(
    payload.servizi,
    payload.serviziSegments,
    poltronaId,
  );
  const daCliente = segmentValue(payload.disdetteSegments?.daCliente, poltronaId);
  const daSalone = segmentValue(payload.disdetteSegments?.daSalone, poltronaId);
  // La distribuzione nel team resta completa; solo il totale KPI segue la poltrona.
  const team = Array.isArray(payload.ricorrenti?.team) ? payload.ricorrenti.team : [];
  const selectedMember = team.find((member) => String(member.id) === String(poltronaId));
  const ricorrentiTotale = selectedMember
    ? toNumber(selectedMember.count)
    : toNumber(payload.ricorrenti?.totale);
  const totaleDisdette = daCliente + daSalone;

  return {
    ...payload,
    canali,
    servizi,
    canaliSegments: null,
    serviziSegments: null,
    disdetteSegments: null,
    disdette: {
      totale: totaleDisdette,
      daCliente,
      daSalone,
      prenotazioniTotale: toNumber(payload.disdette?.prenotazioniTotale),
    },
    disdetteSummary: totaleDisdette
      ? [
          ['Da cliente', Math.round((daCliente / totaleDisdette) * 100), 'disdetteCliente'],
          ['Da salone', Math.round((daSalone / totaleDisdette) * 100), 'disdetteSalone'],
        ]
      : [],
    ricorrenti: {
      ...payload.ricorrenti,
      totale: ricorrentiTotale,
      team,
    },
  };
};

/** Riduce il payload bookings (e prev) a una sola poltrona usando i segments. */
export const filterBookingsPayloadByPoltrona = (data, poltronaId) => {
  if (!data || !poltronaId || poltronaId === 'tutte') return data;
  return {
    ...filterBookingsSectionByPoltrona(data, poltronaId),
    prev: data.prev ? filterBookingsSectionByPoltrona(data.prev, poltronaId) : null,
  };
};

const filterQueueSectionByPoltrona = (payload, poltronaId) => {
  if (!payload) return payload;
  const queue = (payload.queue || []).map((row) => {
    const q = segmentValue(row.queueSegments, poltronaId);
    const r = segmentValue(row.recoveredSegments, poltronaId);
    return {
      ...row,
      queue: q,
      recovered: r,
      queueSegments: null,
      recoveredSegments: null,
    };
  });
  const totalQueue = queue.reduce((sum, row) => sum + toNumber(row.queue), 0);
  const totalRecovered = queue.reduce((sum, row) => sum + toNumber(row.recovered), 0);
  const summaryTotal = totalQueue + totalRecovered;

  return {
    ...payload,
    queue,
    queueSummary: {
      ...(payload.queueSummary || {}),
      totalQueue,
      totalRecovered,
      items: summaryTotal
        ? [
            ['In coda', Math.round((totalQueue / summaryTotal) * 100), 'queue'],
            ['Recuperati', Math.round((totalRecovered / summaryTotal) * 100), 'recovered'],
          ]
        : [
            ['In coda', 0, 'queue'],
            ['Recuperati', 0, 'recovered'],
          ],
      queueSegments: null,
      recoveredSegments: null,
    },
  };
};

export const filterQueuePayloadByPoltrona = (data, poltronaId) => {
  if (!data || !poltronaId || poltronaId === 'tutte') return data;
  return {
    ...filterQueueSectionByPoltrona(data, poltronaId),
    prev: data.prev ? filterQueueSectionByPoltrona(data.prev, poltronaId) : null,
  };
};

export const mergeStatisticsScope = (
  financeData,
  bookingsData,
  queueData,
  { compareKey, poltronaId } = {},
) => {
  const bookings = filterBookingsPayloadByPoltrona(bookingsData, poltronaId);
  const queue = filterQueuePayloadByPoltrona(queueData, poltronaId);
  return {
    ...EMPTY_STATISTICS_SCOPE,
    ...mapFinanceToScope(financeData, { compareKey }),
    ...mapBookingsToScope(bookings, { compareKey }),
    ...mapQueueToScope(queue, { compareKey }),
  };
};
